<template>
  <div class="pa-6">
    <!-- Árbol -->
    <v-card class="tree-container rounded-lg elevation-2 pa-4">
      <v-row dense align="center" class="mb-4">
        <v-col cols="12" :md="modoBloqueadoQR ? 12 : 7" class="d-flex align-center">
          <!-- Crear armario raíz: botón aparte para que no se confunda con el buscador -->
          <v-tooltip v-if="puedeEditar && !modoBloqueadoQR" bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                class="mr-2 accion-minimal"
                aria-label="Nuevo armario"
                v-bind="attrs"
                v-on="on"
                @click="abrirDialogoNuevo"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </template>
            <span>Nuevo armario raíz</span>
          </v-tooltip>

          <v-tooltip v-if="puedeEditar && !modoBloqueadoQR" bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                class="mr-2 accion-minimal"
                :class="{ 'accion-activa': seleccionMultiple }"
                aria-label="Selección múltiple"
                :aria-pressed="String(seleccionMultiple)"
                v-bind="attrs"
                v-on="on"
                @click="alternarSeleccionMultiple"
              >
                <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
              </v-btn>
            </template>
            <span>Selección múltiple (categoría o mover varios a la vez)</span>
          </v-tooltip>

          <v-text-field
            v-model="searchQuery"
            prepend-inner-icon="mdi-magnify"
            label="Buscar armario por nombre..."
            outlined
            dense
            clearable
            class="white elevation-1 rounded-lg"
            hide-details
            @click:clear="onClearSearch"
          />
        </v-col>

        <!-- Filtro por categoría -->
        <v-col v-if="!modoBloqueadoQR" cols="12" md="5" class="d-flex align-center">
          <v-select
            v-model="categoriaFiltro"
            :items="categorias"
            item-text="nombre"
            item-value="id"
            label="Filtrar por categoría"
            prepend-inner-icon="mdi-palette-outline"
            :no-data-text="'No hay categorías'"
            outlined
            dense
            clearable
            hide-details
            class="white elevation-1 rounded-lg"
          >
            <template #selection="{ item }">
              <span class="color-swatch mr-2" :style="{ backgroundColor: item.color }"></span>
              {{ item.nombre }}
            </template>
            <template #item="{ item }">
              <span class="color-swatch mr-3" :style="{ backgroundColor: item.color }"></span>
              {{ item.nombre }}
            </template>
          </v-select>

          <v-tooltip v-if="puedeGestionarCategorias" bottom open-delay="300">
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                class="ml-1 accion-minimal"
                aria-label="Gestionar categorías"
                v-bind="attrs"
                v-on="on"
                @click="$emit('gestionar-categorias')"
              >
                <v-icon>mdi-cog-outline</v-icon>
              </v-btn>
            </template>
            <span>Gestionar categorías</span>
          </v-tooltip>

        </v-col>
      </v-row>

      <!-- Acciones en bloque sobre los armarios marcados -->
      <v-expand-transition>
        <div v-if="seleccionMultiple" class="barra-bloque d-flex align-center flex-wrap mb-3">
          <v-icon small color="primary" class="mr-2">mdi-checkbox-multiple-marked-outline</v-icon>
          <span class="texto-bloque">
            <template v-if="marcados.length">
              <strong>{{ marcados.length }}</strong> armario{{ marcados.length > 1 ? "s" : "" }} marcado{{ marcados.length > 1 ? "s" : "" }}
            </template>
            <template v-else>Pulsa los armarios para marcarlos</template>
          </span>
          <v-spacer />
          <v-menu offset-y :disabled="!marcados.length">
            <template #activator="{ on, attrs }">
              <v-btn small text color="primary" class="text-none" :disabled="!marcados.length" v-bind="attrs" v-on="on">
                <v-icon left small>mdi-palette-outline</v-icon> Categoría
              </v-btn>
            </template>
            <v-list dense>
              <v-list-item v-for="cat in categorias" :key="cat.id" @click="$emit('categoria-en-bloque', { ids: marcados, id_categoria: cat.id })">
                <SimboloCategoria :icono="cat.icono" :color="cat.color" :tamano="16" class="mr-3" />
                <v-list-item-title>{{ cat.nombre }}</v-list-item-title>
              </v-list-item>
              <v-divider v-if="categorias.length" />
              <v-list-item @click="$emit('categoria-en-bloque', { ids: marcados, id_categoria: null })">
                <v-icon small class="mr-3">mdi-close-circle-outline</v-icon>
                <v-list-item-title class="grey--text text--darken-1">Quitar categoría</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
          <v-btn v-if="puedeGestionarCategorias" small text color="primary" class="text-none" :disabled="!marcados.length" @click="$emit('carpetas-en-bloque', marcados)">
            <v-icon left small>mdi-folder-plus-outline</v-icon> Carpetas
          </v-btn>
          <v-btn small text color="primary" class="text-none" :disabled="!marcados.length" @click="$emit('mover-en-bloque', marcados)">
            <v-icon left small>mdi-file-tree-outline</v-icon> Mover a…
          </v-btn>
          <v-btn small text class="text-none" :disabled="!marcados.length" @click="marcados = []">Desmarcar</v-btn>
          <v-btn small icon aria-label="Salir de la selección múltiple" @click="alternarSeleccionMultiple"><v-icon small>mdi-close</v-icon></v-btn>
        </div>
      </v-expand-transition>

      <!-- Lienzo del árbol: scroll propio (la tarjeta no crece), arrastrar el fondo para moverse y zoom -->
      <div v-if="filteredTree && filteredTree.length > 0" class="arbol-marco">
      <div
        ref="lienzo"
        class="arbol-lienzo"
        :class="{ 'arbol-arrastrando': pan.moviendo }"
        :style="{ height: `${altoLienzo}px` }"
        @mousedown="iniciarPan"
        @wheel="zoomConRueda"
      >
        <div ref="contenido" class="arbol-contenido" :style="{ zoom }">
        <TreeNode
          v-for="node in filteredTree"
          :key="node.id"
          :node="node"
          :usuarios="usuarios"
          :selectedNode="selectedNode"
          :search-query="searchQuery"
          :verPlanos="verPlanos"
          :puedeEditar="puedeEditar"
          :puedeAdjuntar="puedeAdjuntar"
          :puedeOrdenar="puedeOrdenar && !modoBloqueadoQR"
          :seleccionMultiple="seleccionMultiple"
          :marcados="marcados"
          @marcar="alternarMarcado"
          @cambiar-padre="$emit('cambiar-padre', $event)"
          @ordenar-hijos="$emit('ordenar-hijos', $event)"
          @select="$emit('select-node', $event)"
          @add-subnode="$emit('add-subnode', $event)"
          @edit-title="$emit('edit-title', $event)"
          @remove-node="$emit('remove-node', $event)"
          @attach-file="$emit('attach-file', $event)"
          @view-files="$emit('view-files', $event)"
          @ver-plano="$emit('ver-plano', $event)"
          @toggle="$emit('toggle', $event)"
          @ver-qr="$emit('ver-qr', $event)"
          @asignar-categoria="$emit('asignar-categoria', $event)"
          @editar-nota="$emit('editar-nota', $event)"
        />
        </div>
      </div>

        <!-- Controles para moverse por el árbol (fuera del scroll: siempre en la esquina) -->
        <div class="arbol-controles elevation-3" @mousedown.stop>
          <v-tooltip v-for="c in controles" :key="c.etiqueta" top open-delay="300">
            <template #activator="{ on, attrs }">
              <v-btn icon small :aria-label="c.etiqueta" :disabled="c.desactivado" v-bind="attrs" v-on="on" @click="c.accion">
                <v-icon small>{{ c.icono }}</v-icon>
              </v-btn>
            </template>
            <span>{{ c.etiqueta }}</span>
          </v-tooltip>
          <span class="arbol-zoom-valor" title="Zoom (Ctrl + rueda)">{{ Math.round(zoom * 100) }}%</span>
        </div>
      </div>

      <div v-else-if="errorCarga" class="text-center pa-12 grey--text text--darken-1">
        <v-icon size="64" color="orange lighten-2">mdi-cloud-alert-outline</v-icon>
        <p class="mt-4 mb-1 font-weight-bold">No se han podido cargar los armarios</p>
        <p class="mb-3 text-body-2">{{ errorCarga }}</p>
        <v-btn color="primary" depressed class="text-none" :loading="cargando" @click="$emit('reintentar-carga')">
          <v-icon left>mdi-refresh</v-icon> Reintentar
        </v-btn>
      </div>

      <div v-else-if="hayFiltros" class="text-center pa-12 grey--text">
        <v-icon size="64" color="grey lighten-2">mdi-filter-remove-outline</v-icon>
        <p class="mt-4">Ningún armario coincide con los filtros</p>
      </div>

      <div v-else class="text-center pa-12 grey--text">
        <v-icon size="64" color="grey lighten-2">mdi-family-tree</v-icon>
        <template v-if="puedeEditar && !modoBloqueadoQR">
          <p class="mt-4 mb-2">Todavía no hay armarios. Empieza creando el primero.</p>
          <v-btn color="primary" depressed class="text-none" @click="abrirDialogoNuevo">
            <v-icon left>mdi-plus</v-icon> Crear un armario
          </v-btn>
        </template>
        <p v-else class="mt-4">Todavía no hay armarios registrados.</p>
      </div>
    </v-card>

    <!-- Nuevo armario raíz -->
    <v-dialog v-model="dialogoNuevo" max-width="440">
      <v-card class="rounded-xl">
        <v-card-title class="subtitle-1 font-weight-bold">
          <v-icon left color="primary">mdi-plus-circle-outline</v-icon>
          Nuevo armario raíz
        </v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="nuevoArmario"
            label="Nombre del armario"
            outlined
            dense
            autofocus
            @keyup.enter="emitCrearArmario"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogoNuevo = false">Cancelar</v-btn>
          <v-btn color="primary" depressed :disabled="!nuevoArmario.trim()" @click="emitCrearArmario">
            Crear
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import TreeNode from "@/components/TreeNode.vue";
import SimboloCategoria from "@/components/utils/SimboloCategoria.vue";
import { mapState } from "vuex";

const ZOOM_MIN = 0.4;
const ZOOM_MAX = 1.5;
const ZOOM_PASO = 0.1;
const ALTO_MINIMO = 320;
const CLAVE_ZOOM = "armarios.zoomArbol";

function leerZoomGuardado() {
  try {
    const valor = parseFloat(localStorage.getItem(CLAVE_ZOOM));
    return valor >= ZOOM_MIN && valor <= ZOOM_MAX ? valor : 1;
  } catch (e) {
    return 1;
  }
}

export default {
  name: "ArmariosTreeView",
  components: { TreeNode, SimboloCategoria },
  props: {
    treeData: { type: Array, default: () => [] },
    errorCarga: { type: String, default: "" },
    cargando: { type: Boolean, default: false },
    usuarios: { type: Array, default: () => [] },
    selectedNode: { type: Object, default: null },
    searchQueryId: { type: String, default: "" },

    verPlanos: {type: Boolean, default: true},
    puedeEditar: {type: Boolean, default: true},
    puedeAdjuntar: {type: Boolean, default: true},
    modoBloqueadoQR: {type: Boolean, default: false},
    puedeGestionarCategorias: {type: Boolean, default: false},
    puedeOrdenar: {type: Boolean, default: false},
  },
  data() {
    return {
      searchQuery: "",
      categoriaFiltro: null,
      // acciones en bloque
      seleccionMultiple: false,
      marcados: [],
      nuevoArmario: "",
      dialogoNuevo: false,

      // lienzo del árbol
      altoLienzo: 500,
      zoom: leerZoomGuardado(),
      pan: { activo: false, moviendo: false, x: 0, y: 0, scrollLeft: 0, scrollTop: 0 },
    };
  },
  mounted() {
    window.addEventListener("resize", this.ajustarAlto);
    window.addEventListener("mousemove", this.moverPan);
    window.addEventListener("mouseup", this.terminarPan);
    this.$nextTick(this.ajustarAlto);
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.ajustarAlto);
    window.removeEventListener("mousemove", this.moverPan);
    window.removeEventListener("mouseup", this.terminarPan);
  },
  watch: {
    // al seleccionar un nodo (también desde el lateral) el árbol se desplaza hasta él
    selectedNode(nodo) {
      if (nodo) setTimeout(() => this.irAlSeleccionado(), 350); // tras desplegar sus padres
    },
    zoom(valor) {
      try { localStorage.setItem(CLAVE_ZOOM, String(valor)); } catch (e) { /* sin almacenamiento */ }
    },
    // el lienzo aparece/desaparece con los filtros: recalcular su alto
    hayArbol() {
      this.$nextTick(this.ajustarAlto);
    },
    // si se borra la categoría filtrada, quitamos el filtro
    categorias(lista) {
      if (this.categoriaFiltro && !lista.some((c) => c.id === this.categoriaFiltro)) this.categoriaFiltro = null;
    },
    searchQueryId: {
      handler(newVal) {
        this.searchQuery = newVal;
      },
      immediate: true,
    },
  },
  computed: {
    ...mapState("armarios", ["categorias", "categoriaPorArmario"]),

    hayArbol() {
      return !!(this.filteredTree && this.filteredTree.length);
    },
    controles() {
      return [
        { etiqueta: "Alejar", icono: "mdi-magnify-minus-outline", accion: () => this.cambiarZoom(-ZOOM_PASO), desactivado: this.zoom <= ZOOM_MIN },
        { etiqueta: "Acercar", icono: "mdi-magnify-plus-outline", accion: () => this.cambiarZoom(ZOOM_PASO), desactivado: this.zoom >= ZOOM_MAX },
        { etiqueta: "Encajar en pantalla", icono: "mdi-fit-to-screen-outline", accion: this.encajar },
        { etiqueta: "Tamaño real (100%)", icono: "mdi-magnify-scan", accion: () => (this.zoom = 1), desactivado: this.zoom === 1 },
        { etiqueta: "Ir al seleccionado", icono: "mdi-crosshairs-gps", accion: this.irAlSeleccionado, desactivado: !this.selectedNode },
        { etiqueta: "Plegar todo", icono: "mdi-unfold-less-horizontal", accion: () => this.$emit("plegar-todo") },
        { etiqueta: "Desplegar todo", icono: "mdi-unfold-more-horizontal", accion: () => this.$emit("desplegar-todo") },
        ...(this.puedeOrdenar && !this.modoBloqueadoQR && (this.treeData || []).length > 1
          ? [{ etiqueta: "Ordenar armarios principales", icono: "mdi-sort", accion: () => this.$emit("ordenar-raices") }]
          : []),
      ];
    },
    hayFiltros() {
      return !!(this.searchQuery && this.searchQuery.trim()) || !!this.categoriaFiltro;
    },

    // Se muestran los armarios que cumplen búsqueda y categoría, junto con sus padres para no perder la jerarquía
    filteredTree() {
      const raw = (this.searchQuery || "").trim();
      const query = raw.toLowerCase();

      const byId= query.startsWith("$");
      const q = byId ? query.slice(1).trim() : query;
      const categoria = this.categoriaFiltro;
      if (!q && !categoria) return this.treeData;


      const filterNodes = (nodes) => {
        return nodes.reduce((acc, node) => {
          const filteredChildren = node.children ? filterNodes(node.children) : [];
          
          const name = (node.name || "").toLowerCase();
          const idStr = String(node.id ?? "");

          const matchesTexto = !q || (byId ? idStr.includes(q) : name.includes(q));
          const matchesCategoria = !categoria || this.categoriaPorArmario[node.id] === categoria;
          const matches = matchesTexto && matchesCategoria;

          if (matches || filteredChildren.length > 0) {
            acc.push({
              ...node,
              children: filteredChildren,
              expanded: true, // forzar expandido en búsqueda
            });
          }
          return acc;
        }, []);
      };

      return filterNodes(this.treeData);
    },
  },
  methods: {
    alternarSeleccionMultiple() {
      this.seleccionMultiple = !this.seleccionMultiple;
      this.marcados = [];
      if (this.seleccionMultiple) this.$emit("clear-selected");
    },
    alternarMarcado(nodo) {
      this.marcados = this.marcados.includes(nodo.id)
        ? this.marcados.filter((id) => id !== nodo.id)
        : [...this.marcados, nodo.id];
    },
    /** Lo llama la página cuando termina una acción en bloque */
    terminarAccionEnBloque() {
      this.marcados = [];
    },
    onClearSearch(){
      this.searchQuery = "";
      this.$emit("update:searchQueryId","");
    },
    // ---------------- lienzo del árbol ----------------
    /** El lienzo ocupa hasta el final de la ventana: el árbol se desplaza dentro y la página no crece */
    ajustarAlto() {
      const lienzo = this.$refs.lienzo;
      if (!lienzo) return;
      const margenInferior = 40;
      this.altoLienzo = Math.max(ALTO_MINIMO, window.innerHeight - lienzo.getBoundingClientRect().top - margenInferior);
    },
    cambiarZoom(delta) {
      const nuevo = Math.round((this.zoom + delta) * 100) / 100;
      this.zoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, nuevo));
    },
    zoomConRueda(evento) {
      if (!evento.ctrlKey) return; // sin Ctrl la rueda desplaza
      evento.preventDefault();
      this.cambiarZoom(evento.deltaY < 0 ? ZOOM_PASO : -ZOOM_PASO);
    },
    /** Zoom para que todo el árbol desplegado quepa en el lienzo (sin pasar del 100%) */
    encajar() {
      const { lienzo, contenido } = this.$refs;
      if (!lienzo || !contenido) return;
      const ancho = contenido.scrollWidth, alto = contenido.scrollHeight; // ya escalados por el zoom actual
      const escala = Math.min(lienzo.clientWidth / ancho, lienzo.clientHeight / alto) * this.zoom;
      this.zoom = Math.min(1, Math.max(ZOOM_MIN, Math.floor(escala * 20) / 20));
      lienzo.scrollTo({ left: 0, top: 0, behavior: "smooth" });
    },
    /** Centra el nodo seleccionado moviendo solo el lienzo (scrollIntoView movería también la página) */
    irAlSeleccionado() {
      const { lienzo } = this.$refs;
      const tarjeta = this.$el.querySelector(".selected-node-wrapper .node-card");
      if (!lienzo || !tarjeta) return;
      const l = lienzo.getBoundingClientRect();
      const t = tarjeta.getBoundingClientRect();
      lienzo.scrollBy({
        left: t.left + t.width / 2 - (l.left + l.width / 2),
        top: t.top + t.height / 2 - (l.top + l.height / 2),
        behavior: "smooth",
      });
    },
    /** Arrastrar el fondo para moverse (como un mapa). Un clic sin arrastrar en el fondo quita la selección */
    iniciarPan(evento) {
      if (evento.button !== 0 || evento.target.closest(".node-wrapper, button, a, input")) return;
      const { lienzo } = this.$refs;
      this.pan = { activo: true, moviendo: false, x: evento.clientX, y: evento.clientY, scrollLeft: lienzo.scrollLeft, scrollTop: lienzo.scrollTop };
    },
    moverPan(evento) {
      if (!this.pan.activo) return;
      const dx = evento.clientX - this.pan.x, dy = evento.clientY - this.pan.y;
      if (!this.pan.moviendo && Math.hypot(dx, dy) < 4) return;
      this.pan.moviendo = true;
      this.$refs.lienzo.scrollLeft = this.pan.scrollLeft - dx;
      this.$refs.lienzo.scrollTop = this.pan.scrollTop - dy;
    },
    terminarPan() {
      if (!this.pan.activo) return;
      if (!this.pan.moviendo) this.$emit("clear-selected");
      this.pan = { ...this.pan, activo: false, moviendo: false };
    },

    abrirDialogoNuevo() {
      this.nuevoArmario = "";
      this.dialogoNuevo = true;
    },
    emitCrearArmario() {
      const name = (this.nuevoArmario || "").trim();
      if (!name) return;
      this.$emit("crear-armario-raiz", name);
      this.nuevoArmario = "";
      this.dialogoNuevo = false;
    },
  },
};
</script>

<style scoped>
/* Botones de acción de solo icono (nuevo armario, categorías): discretos y en color al pasar el ratón */
.accion-minimal {
  color: #607d8b !important;
  transition: color 0.2s ease, transform 0.2s ease;
}
.accion-activa {
  color: #117f9c !important;
  background-color: rgba(17, 127, 156, 0.14) !important;
}
.barra-bloque {
  gap: 4px;
  padding: 6px 10px;
  border-radius: 10px;
  background: #e6f3f6;
  border: 1px solid #b5dbe4;
}
.texto-bloque {
  font-size: 0.85rem;
  color: #37474f;
}
.accion-minimal:hover {
  color: #117f9c !important;
  transform: scale(1.08);
}

.tree-container {
  background-color: white;
  border: 1px solid #e0e4e9;
}

/* Lienzo del árbol: scroll propio en las dos direcciones, fondo de puntos y arrastre */
.arbol-lienzo {
  position: relative;
  overflow: auto;
  border-radius: 10px;
  border: 1px solid #edf1f4;
  background-color: #fbfcfd;
  background-image: radial-gradient(circle, #dfe6eb 1px, transparent 1px);
  background-size: 22px 22px;
  cursor: grab;
  scrollbar-width: thin;
  scrollbar-color: #c3d0d8 transparent;
}
.arbol-lienzo::-webkit-scrollbar { width: 10px; height: 10px; }
.arbol-lienzo::-webkit-scrollbar-thumb { background: #c3d0d8; border-radius: 10px; border: 2px solid #fbfcfd; }
.arbol-lienzo::-webkit-scrollbar-track { background: transparent; }
.arbol-arrastrando {
  cursor: grabbing;
  user-select: none;
}
.arbol-arrastrando >>> .node-card {
  cursor: grabbing;
}
/* El contenido crece con el árbol (y el lienzo muestra scroll); abajo deja sitio al menú del nodo */
.arbol-contenido {
  display: inline-block;
  min-width: 100%;
  padding: 8px 24px 140px 16px;
}

/* Controles flotantes (zoom, encajar, ir al seleccionado, plegar/desplegar) */
.arbol-marco {
  position: relative;
}
.arbol-controles {
  position: absolute;
  bottom: 14px;
  right: 18px;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 4px 8px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #e3e9ee;
  cursor: default;
  z-index: 4;
}
.arbol-controles .v-btn {
  color: #607d8b !important;
}
.arbol-controles .v-btn:hover {
  color: #117f9c !important;
}
.arbol-zoom-valor {
  min-width: 42px;
  text-align: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #607d8b;
}
.color-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.15);
  flex-shrink: 0;
}
</style>